<template>
  <div class="mx-2">
    <Select
      :id="id"
      v-model="localValue"
      :options="formattedOptions"
      :placeholder="label"
      optionLabel="label"
      optionValue="value"
      :filter="filter"
      :class="[
        'min-w-fit',
        bgColor === 'primary' ? '!bg-primary-900 text-white' : 'bg-white text-gray-500'
      ]"
      :pt="{
        root: {
          class: [
            'relative inline-flex items-center border h-10 cursor-pointer rounded-lg p-2 text-sm justify-between min-w-fit',
            bgColor === 'primary' ? 'bg-primary-900 text-white' : 'bg-white text-gray-500'
          ]
        },
        trigger: {
          class: 'w-full h-full flex items-center justify-between'
        },
        label: {
          class: [
            'mr-2 text-xs',
            bgColor === 'primary' ? 'text-white' : 'text-gray-500'
          ]
        },
        value: {
          class: [
            'mr-2',
            bgColor === 'primary' ? 'text-white' : ''
          ]
        },
        icon: {
          class: [
            'px-4',
            bgColor === 'primary' ? 'text-white' : 'text-gray-500'
          ]
        },
        panel: {
          class: 'z-50'
        },
        item: {
          class: 'hover:bg-gray-100 p-2 cursor-pointer'
        }
      }"
      @change="handleChange"
    >
      <template #value>
        <div class="flex items-center justify-between w-full gap-4">
          <span :class="['mr-2 text-xs', bgColor === 'primary' ? 'text-white' : 'text-gray-500']">
            {{ label }}
          </span>
          <span :class="[bgColor === 'primary' ? 'text-white' : '']">
            {{  displayValue }}
          </span>
        </div>
      </template>
    </Select>
  </div>
</template>

<script>
import { Select } from 'primevue';

export default {
  name: 'FilterSelect',
  components: {
    Select
  },
  props: {
    modelValue: {
      type: [String, Number, Object],
      default: ''
    },
    options: {
      type: Array,
      required: true
    },
    label: {
      type: String,
      required: true
    },
    id: {
      type: String,
      required: true
    },
    bgColor: {
      type: String,
      default: 'white',
      validator: (value) => ['white', 'primary'].includes(value)
    },
    valueKey: {
      type: String,
      default: 'value'
    },
    labelKey: {
      type: String,
      default: 'label'
    },
    allOption: {
      type: Object,
      default: null
    },
    filter: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'change'],
  computed: {
    localValue: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      }
    },
    formattedOptions() {
      const formatted = [];
      
      // Add "All" option if provided
      if (this.allOption) {
        formatted.push({
          label: this.allOption.label,
          value: this.allOption.value
        });
      }
      
      // Add regular options
      this.options.forEach(option => {
        if (typeof option === 'object' && option !== null) {
          // If option is an object with value and label properties
          if (option.value !== undefined) {
            formatted.push({
              label: option.label || option[this.labelKey] || option,
              value: option.value
            });
          } else {
            // If option is an object but no value property, use the object itself as value
            formatted.push({
              label: option.label || option[this.labelKey] || option,
              value: option
            });
          }
        } else {
          // If option is a primitive (string, number), use it as both label and value
          formatted.push({
            label: option,
            value: option
          });
        }
      });
      
      return formatted;
    },
    displayValue() {
      // If allOption is provided and the value matches allOption value, show allOption label
      if (this.allOption && this.modelValue == this.allOption.value) {
        return this.allOption.label;
      }
      
      // If no value is selected or value is empty/null, show allOption label if available
      if (this.modelValue === null || this.modelValue === undefined || this.modelValue === '') {
        return this.allOption ? this.allOption.label : '';
      }
      
      // Find the matching option from the formatted options
      const option = this.formattedOptions.find(opt => opt.value == this.modelValue);

      if (option) {
        return option.label;
      }
      
      // Fallback: if no matching option found, show the value itself
      return this.modelValue;
    }
  },
  methods: {
    handleChange(event) {
      this.$emit('change', event.value);
    }
  }
};
</script>

<style scoped>
/* Custom styles for PrimeVue Dropdown */
:deep(.p-dropdown) {
  border: none;
  background: transparent;
}

:deep(.p-dropdown:not(.p-disabled).p-focus) {
  box-shadow: none;
}

:deep(.p-dropdown-panel) {
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

:deep(.p-dropdown-items-wrapper) {
  max-height: 200px;
}

:deep(.p-dropdown-item) {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
}

:deep(.p-dropdown-item:hover) {
  background-color: #f3f4f6;
}

:deep(.p-dropdown-item.p-highlight) {
  background-color: #3b82f6;
  color: white;
}
</style> 